import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, timeout } from "rxjs/operators";

import { ApiService } from "@bitwarden/common/abstractions/api.service";

import { getGroceryConfig } from "./grocery.config";
import { GROCERY_TIMEOUT_MS, withGroceryAuth } from "./grocery-http";
import {
  NewShoppingListItem,
  ShoppingList,
  ShoppingListItemChanges,
  ShoppingListResponse,
  ShoppingListsResponse,
  ShoppingListStatus,
} from "./shopping-list.types";

/**
 * Client for the persistent per-user shopping lists (Bucket A) served by
 * gloss-grocery under `/v1/grocery/lists`. Every call carries the gloss-api
 * bearer token; the backend scopes results to that user. Write calls return the
 * whole updated list, so callers just replace their state with the response.
 */
@Injectable({ providedIn: "root" })
export class ShoppingListService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
  ) {}

  /** The lists API shares the grocery root and endpoint config with items/prices. */
  private baseUrl(): string {
    return `${getGroceryConfig().url}${getGroceryConfig().endpoint.lists}`;
  }

  /** Auth wiring is shared with {@link GroceryService}; every call is time-bounded. */
  private withAuth<T>(call: (headers: HttpHeaders) => Observable<T>): Observable<T> {
    return withGroceryAuth(this.apiService, call).pipe(timeout(GROCERY_TIMEOUT_MS));
  }

  /** All of the user's lists, optionally filtered by status, newest-touched first. */
  getLists(status?: ShoppingListStatus): Observable<ShoppingList[]> {
    return this.withAuth((headers) => {
      let params = new HttpParams();
      if (status) {
        params = params.set("status", status);
      }
      return this.http
        .get<ShoppingListsResponse>(this.baseUrl(), { headers, params })
        .pipe(map((r) => r.lists));
    });
  }

  getList(listId: string): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .get<ShoppingListResponse>(`${this.baseUrl()}/${encodeURIComponent(listId)}`, { headers })
        .pipe(map((r) => r.list)),
    );
  }

  createList(
    input: { name?: string; items?: NewShoppingListItem[] } = {},
  ): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .post<ShoppingListResponse>(this.baseUrl(), input, { headers })
        .pipe(map((r) => r.list)),
    );
  }

  updateList(
    listId: string,
    changes: { name?: string; status?: ShoppingListStatus },
  ): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .patch<ShoppingListResponse>(`${this.baseUrl()}/${encodeURIComponent(listId)}`, changes, {
          headers,
        })
        .pipe(map((r) => r.list)),
    );
  }

  /** Archive a finished list (kept for history / copy-over). */
  archiveList(listId: string): Observable<ShoppingList> {
    return this.updateList(listId, { status: "archived" });
  }

  deleteList(listId: string): Observable<{ deleted: boolean }> {
    return this.withAuth((headers) =>
      this.http.delete<{ deleted: boolean }>(`${this.baseUrl()}/${encodeURIComponent(listId)}`, {
        headers,
      }),
    );
  }

  addItem(listId: string, item: NewShoppingListItem): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .post<ShoppingListResponse>(`${this.baseUrl()}/${encodeURIComponent(listId)}/items`, item, {
          headers,
        })
        .pipe(map((r) => r.list)),
    );
  }

  updateItem(
    listId: string,
    itemId: string,
    changes: ShoppingListItemChanges,
  ): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .patch<ShoppingListResponse>(
          `${this.baseUrl()}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
          changes,
          { headers },
        )
        .pipe(map((r) => r.list)),
    );
  }

  /** Tick an item off (or un-tick it). */
  setItemPurchased(listId: string, itemId: string, purchased: boolean): Observable<ShoppingList> {
    return this.updateItem(listId, itemId, { purchased });
  }

  removeItem(listId: string, itemId: string): Observable<ShoppingList> {
    return this.withAuth((headers) =>
      this.http
        .delete<ShoppingListResponse>(
          `${this.baseUrl()}/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
          { headers },
        )
        .pipe(map((r) => r.list)),
    );
  }

  /** Copy selected items into a brand-new active list (archive copy-over flow). */
  copyToNewList(name: string | undefined, items: NewShoppingListItem[]): Observable<ShoppingList> {
    return this.createList({ name, items });
  }

  /**
   * Records a search term that returned no catalog matches so the team can add
   * it to the shared catalog later. Aggregate/anonymous (no user data) — the
   * backend upserts the term and counts it. Fire-and-forget: callers must ignore
   * the result and never let a failure affect the add/search UX.
   */
  reportNotFound(term: string): Observable<void> {
    return this.withAuth((headers) =>
      this.http.post<void>(`${getGroceryConfig().url}/v1/grocery/not-found`, { term }, { headers }),
    );
  }
}
